/**
 * React hook for Phantasy Live2D SDK
 * Provides easy integration with React components
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { PhantasyLive2D } from "../core/PhantasyLive2D";
import type { PhantasyLive2DConfig } from "../types/config";

interface UsePhantasyLive2DOptions
  extends Omit<PhantasyLive2DConfig, "container"> {
  containerId: string;
  autoInitialize?: boolean;
}

interface UsePhantasyLive2DReturn {
  phantasy: PhantasyLive2D | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  speak: (text: string) => Promise<void>;
  setExpression: (expression: string) => void;
  stopSpeech: () => void;
  initialize: () => Promise<void>;
  destroy: () => void;
}

export function usePhantasyLive2D(
  options: UsePhantasyLive2DOptions,
): UsePhantasyLive2DReturn {
  const [phantasy, setPhantasy] = useState<PhantasyLive2D | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const phantasyRef = useRef<PhantasyLive2D | null>(null);
  const initializationAttempted = useRef(false);

  const { containerId, autoInitialize = true, ...config } = options;

  // Create Phantasy instance
  useEffect(() => {
    // IMPORTANT: Only create instance once per container
    // Don't recreate on every config change to prevent event listener loss
    if (phantasyRef.current) return;

    try {
      const phantasyInstance = new PhantasyLive2D({
        ...config,
        container: `#${containerId}`,
        onReady: () => {
          console.log('[usePhantasyLive2D] onReady callback fired');
          setIsInitialized(true);
          setIsLoading(false);
          setError(null);
        },
        onError: (err) => {
          console.error('[usePhantasyLive2D] onError callback fired:', err.message);
          setError(err.message);
          setIsLoading(false);
          setIsInitialized(false);
        },
      });

      // Set up event listeners with logging
      const handleError = (err: Error) => {
        console.error('[usePhantasyLive2D] error event fired:', err.message);
        setError(err.message);
      };

      const handleInitialized = () => {
        console.log('[usePhantasyLive2D] initialized event fired - setting state');
        setIsInitialized(true);
        setIsLoading(false);
      };

      phantasyInstance.on("error", handleError);
      phantasyInstance.on("initialized", handleInitialized);

      setPhantasy(phantasyInstance);
      phantasyRef.current = phantasyInstance;
      console.log('[usePhantasyLive2D] PhantasyLive2D instance created');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      console.error('[usePhantasyLive2D] Failed to create instance:', errorMessage);
      setError(errorMessage);
      setIsLoading(false);
    }
    // FIXED: Only depend on containerId, not config
    // Config changes should update the instance, not recreate it
  }, [containerId]);

  // Initialize function (defined before auto-initialize effect)
  const initialize = useCallback(async () => {
    console.log('[usePhantasyLive2D] initialize() called:', {
      hasPhantasy: !!phantasy,
      isInitialized,
      attemptedBefore: initializationAttempted.current,
    });

    if (!phantasy) {
      console.warn('[usePhantasyLive2D] Cannot initialize - no phantasy instance');
      return;
    }

    if (isInitialized) {
      console.warn('[usePhantasyLive2D] Already initialized, skipping');
      return;
    }

    if (initializationAttempted.current) {
      console.warn('[usePhantasyLive2D] Initialization already attempted, skipping');
      return;
    }

    initializationAttempted.current = true;
    setIsLoading(true);
    setError(null);

    console.log('[usePhantasyLive2D] Calling phantasy.initialize()...');
    try {
      await phantasy.initialize();
      console.log('[usePhantasyLive2D] phantasy.initialize() completed successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      console.error('[usePhantasyLive2D] Initialize failed:', errorMessage);
      setError(errorMessage);
      initializationAttempted.current = false;
    }
  }, [phantasy, isInitialized]);

  // Auto-initialize if enabled
  useEffect(() => {
    console.log('[usePhantasyLive2D] Auto-initialize effect triggered:', {
      autoInitialize,
      hasPhantasy: !!phantasy,
      isInitialized,
      attemptedBefore: initializationAttempted.current,
    });

    if (
      autoInitialize &&
      phantasy &&
      !isInitialized &&
      !initializationAttempted.current
    ) {
      console.log('[usePhantasyLive2D] Conditions met, calling initialize()...');
      initialize();
    } else {
      console.log('[usePhantasyLive2D] Skipping auto-initialize:', {
        autoInitialize,
        hasPhantasy: !!phantasy,
        isInitialized,
        attemptedBefore: initializationAttempted.current,
      });
    }
  }, [phantasy, autoInitialize, isInitialized, initialize]);

  // Speak function
  const speak = useCallback(
    async (text: string) => {
      if (!phantasy || !isInitialized) {
        throw new Error("Phantasy SDK not initialized");
      }
      await phantasy.speak(text);
    },
    [phantasy, isInitialized],
  );

  // Set expression function
  const setExpression = useCallback(
    (expression: string) => {
      if (!phantasy || !isInitialized) {
        console.warn("Phantasy SDK not initialized, cannot set expression");
        return;
      }
      phantasy.setExpression(expression);
    },
    [phantasy, isInitialized],
  );

  // Stop speech function
  const stopSpeech = useCallback(() => {
    if (phantasy && isInitialized) {
      phantasy.stopSpeech();
    }
  }, [phantasy, isInitialized]);

  // Destroy function
  const destroy = useCallback(() => {
    if (phantasyRef.current) {
      phantasyRef.current.destroy();
      phantasyRef.current = null;
      setPhantasy(null);
      setIsInitialized(false);
      setIsLoading(false);
      setError(null);
      initializationAttempted.current = false;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      destroy();
    };
  }, [destroy]);

  return {
    phantasy,
    isLoading,
    isInitialized,
    error,
    speak,
    setExpression,
    stopSpeech,
    initialize,
    destroy,
  };
}
